import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {from, Observable, of} from 'rxjs';
import {filter, switchMap, tap, map} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {ITreeState, TreeComponent, TreeNode} from '@ali-hm/angular-tree-component';
import {ItemNode, TreeUtil} from '@emm/core/util/angular-tree-component.util';
import {Consumer} from '@emm/core/models/consumer.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {UserService} from '@emm/core/services/user.service';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {Clipboard} from '@angular/cdk/clipboard';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';



@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-item-detail-tree',
    styleUrls: ['./item-detail-tree.component.scss'],
    templateUrl: './item-detail-tree.component.html'
})
export class ItemDetailTreeComponent implements OnInit, OnChanges, AfterViewInit {

    @Input() expandFirstNode: boolean;
    @Input() isLoading: boolean;
    @Input() items: ItemNode[];
    @Input() options: any;
    @Input() readonly: boolean;
    @Input() selectedConsumer: Consumer;
    @Input() selectedLocation: LocationSet;
    @Input() selectedFeatureScope: string;
    @Input() copyAcrossSubmenu: boolean;

    @Output() addItem = new EventEmitter<TreeNode>();
    @Output() pasteItemCascade = new EventEmitter<TreeNode>();
    @Output() copyItem = new EventEmitter<TreeNode>();
    @Output() deleteItem = new EventEmitter<TreeNode>();
    @Output() deleteItemCascade = new EventEmitter<TreeNode>();
    @Output() moveItem = new EventEmitter<{
        fromPath: string;
        toPath: string;
        index: string;
        onSuccess: () => void;
        dragNode: TreeNode;
    }>();
    @Output() pasteItem = new EventEmitter<TreeNode>();
    @Output() toggleItemDisabled = new EventEmitter<TreeNode>();
    @Output() treeState = new EventEmitter<ITreeState>();

    @ViewChild(TreeComponent) menuTree: TreeComponent;
    isCopied: boolean;
    allowStructureChanges: boolean;
    location: string;
    isAdmin = false;
    newDisableToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.APPLY_LEAST_PRECEDENCE_FOR_ITEM_LOCKS);
    showTags$: Observable<boolean>;

    constructor(
        private clipboard: Clipboard,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private userService: UserService,
        private launchDarklyService: LaunchDarklyService,
        private sharedFacadeService: SharedFacadeService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items'] && RA.isEmptyArray(this.items)) {
            if (this.menuTree) {
                this.menuTree?.treeModel?.collapseAll();
                this.menuTree?.treeModel?.setState({ expandedNodeIds: {} });
            }
        }

        try {
            if (changes['selectedLocation']) {
                const featureScope = changes['selectedFeatureScope'].currentValue;
                this.location = changes['selectedLocation'].currentValue['tag'];
                this.allowStructureChanges = featureScope === FeatureScope.Global || featureScope === 'Viewer';
            }
        } catch (e)  {}
    }

    ngOnInit(): void {
        this.isUserAdmin();
        this.isCopied = this.copyAcrossSubmenu;
        this.showTags$ = this.sharedFacadeService.showTags$.pipe(
            map(showTags => showTags === 'true')
        );
    }

    ngAfterViewInit(): void {
        if (this.expandFirstNode) {
            setTimeout(() => this.menuTree?.treeModel?.roots[0].expand());
        }
    }

    add(node: TreeNode): void {
        this.addItem.emit(node);
    }

    toggleDisabled(node: TreeNode): void {
        this.toggleItemDisabled.emit(node);
    }

    deleteNode(node: TreeNode): void {
        this.deleteItem.emit(node);
    }

    pasteCascade(node: TreeNode): void {
        this.pasteItemCascade.emit(node);
    }

    deleteCascade(node: TreeNode): void {
        this.deleteItemCascade.emit(node);
    }

    copy(node: TreeNode): void {
        this.isCopied = true;
        this.copyItem.emit(node);
    }

    copyTagpath(node: TreeNode): string {
        const item = node.data.data;
        if (item.tagPath) {
            this.clipboard.copy(item.tagPath);
            this.notificationService.success(`Copied: ${item.tagPath}`);
        } else {
            this.notificationService.error('This item does not have a tagpath');
        }
        return item.tagPath;
    }

    paste(node: TreeNode): void {
        this.isCopied = false;
        this.pasteItem.emit(node);
    }

    reloadItems(pathToUpdate: string): Observable<string> {
        const parentPath = TreeUtil.getParentPath(pathToUpdate);
        const parentNode = this.getNode(parentPath);

        return parentNode ? from(parentNode.loadNodeChildren()).pipe(map(() => pathToUpdate)) : of(null);
    }

    reloadItemsOverload(): Observable<string> {
        this.menuTree.treeModel.collapseAll();
        this.menuTree.treeModel.setState({ expandedNodeIds: {} });
        return this.treeFacade.reloadItems();
    }

    reloadItemsAndSetActive(): any {
        return (source: Observable<string>) =>
            source.pipe(
                switchMap((path) => this.reloadItems(path)),
                filter(RA.isNotNil),
                tap((path: string) => {
                    const node = this.getNode(path);
                    node.setIsActive(true);
                })
            );
    }

    handleDragStart() {
        const state = this.menuTree.treeModel.getState();
        this.treeState.emit(state);
    }

    onMoveNode($event): void {
        const dragNode = $event.node;
        const targetParent = $event.to.parent;
        const fromParent = $event.from.parent;
        const treeModel = this.menuTree.treeModel;
        const parentNode: TreeNode = treeModel.getNodeById(targetParent.id);
        const fromNode: TreeNode = treeModel.getNodeById(fromParent.id);
        const index = $event.to.index;
        const fromPath = dragNode.data.tagPath;
        const toPath = targetParent.data.tagPath;

        const onSuccess = () => {
            if (parentNode) {
                parentNode.expand();
                parentNode.loadNodeChildren().then();
            }

            if (fromNode) {
                fromNode.expand();
                fromNode.loadNodeChildren().then();
            }
        };

        this.moveItem.emit({ fromPath, toPath, index, onSuccess, dragNode });
    }

    isSubmenu(itemNode: TreeNode): boolean {
        // submenu > root > null
        return itemNode.parent === null || itemNode.parent.parent === null;
    }

    isUserAdmin(): void {
        this.isAdmin = this.userService.hasAdminRole();
    }

    private getNode(path: string): TreeNode {
        return this.menuTree.treeModel.getNodeBy((item: TreeNode) => item.data.data.jsonPath === path);
    }

    get isEmpty(): boolean {
        return this.items ? this.items.length === 0 : true;
    }

    isCommonItem(node: TreeNode): boolean {
        return node?.data?.data?.commonItem === true;
    }
}
